/*
create trie
insert(string) - boolean
search(string) - boolean
startsWith(string, minmatchlength, doSpellCheck) - string[]
*/

import { getLogger } from '../SearchLogger';

const stringEncoding = 'NFD';
class Trie {
	static instance: Trie;
	character: string = null;
	childNodes: { string?: Trie } = {};
	word = 0;

	constructor() {
		if (Trie.instance) {
			return Trie.instance;
		}
	}

	insert(word: string, options: Record<string, any> = {}): boolean {
		try {
			let currentNode: Trie = this;
			const { ignoreCase = true, locale = 'en-US' } = options;
			const wordToConsider = ignoreCase ? word?.toLocaleLowerCase(locale) : word;
			for (const char of wordToConsider.normalize(stringEncoding)) {
				const charExist = currentNode?.childNodes?.[char]?.character === char.normalize(stringEncoding);
				if (!charExist) {
					const childTrieNode = new Trie();
					childTrieNode.character = char.normalize(stringEncoding);
					currentNode.childNodes[char.normalize(stringEncoding)] = childTrieNode;
				}
				currentNode = currentNode.childNodes[char.normalize(stringEncoding)];
			}
			currentNode.word++;
		} catch (error) {
			getLogger().error('Trie', 'insertion error', error);
			return false;
		}
		return true;
	}

	search(word: string): boolean {
		let currentNode: Trie = this;
		try {
			for (const char of word.normalize(stringEncoding)) {
				const charExist = currentNode?.childNodes?.[char]?.character === char.normalize(stringEncoding);
				if (!charExist) {
					return false;
				}
				currentNode = currentNode.childNodes[char.normalize(stringEncoding)];
			}
		} catch (error) {
			getLogger().error('Trie', 'search error', error);
			return false;
		}
		return Boolean(currentNode.word);
	}

	autoComplete(
		word: string,
		options: AutoCompleteOptions = { minmatchlength: 3, spellCheck: false, spellCheckMinLength: 4 },
	): string[] {
		const matches = [];
		try {
			const { minmatchlength, spellCheck, spellCheckMinLength } = options;
			if (spellCheck && word.length < spellCheckMinLength) {
				return matches;
			}
			if (word.length < minmatchlength) {
				return matches;
			}
			let currentNode: Trie = this;
			let prefix = '';
			let fullMatch = false;
			for (const char of word.normalize(stringEncoding)) {
				const charExist = currentNode?.childNodes?.[char]?.character === char.normalize(stringEncoding);
				if (charExist) {
					prefix += char.normalize(stringEncoding);
					currentNode = currentNode.childNodes[char.normalize(stringEncoding)];
					fullMatch = true;
				} else {
					fullMatch = false;
					break;
				}
			}
			prefix = prefix.slice(0, prefix.length - 1);
			if (spellCheck || fullMatch) {
				const suffices = currentNode.traverse();
				matches.push(...suffices.map((s) => (prefix + s).normalize(stringEncoding)));
			}
		} catch (error: any) {
			getLogger().error('Trie', 'autocomplete error:', error);
			return [];
		}
		return matches;
	}

	traverse(): string[] {
		if (Object.keys(this.childNodes).length) {
			let words = [];
			if (this.word > 0) {
				words.push('');
			}
			for (const key in this.childNodes) {
				const suffices = this.childNodes[key]?.traverse?.() ?? [];
				words = [...words, ...suffices];
			}
			return words.map((w) => (this.character ? this.character + w : w));
		}
		return [this.character];
	}
}

export default Trie;
